import React, { useState, useEffect } from "react";
import Popup from "../Popup2";
import GameOverScreen from "../GameOverScreen";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPause, faPlay } from '@fortawesome/free-solid-svg-icons';
import { updateDocumentInSubcollection } from "../../firestore_sub"; // Import Firestore update function
import { auth } from "../../firebase"; // Import Firebase auth to get UID
import { updateDocument, getDocument } from "../../firestore";
import { MdOutlineRestartAlt } from "react-icons/md";

const WordSearch = () => {
  const words = [
    "REACT",
    "REDUX",
    "FIREBASE",
    "COMPONENT",
    "JAVASCRIPT",
    "HOOK",
    "STATE",
    "PROPS",
    "CONTEXT",
  ];
  const gridSize = 10;

  // Game state variables
  const [grid, setGrid] = useState([]);
  const [selectedCells, setSelectedCells] = useState([]);
  const [foundWords, setFoundWords] = useState([]);
  const [foundCells, setFoundCells] = useState([]);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [gamePaused, setGamePaused] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [nextQuestionTimer, setNextQuestionTimer] = useState(15);
  const [gameStartTime, setGameStartTime] = useState(null);
  const [comboTimer, setComboTimer] = useState(null);
  const [comboMultiplier, setComboMultiplier] = useState(1);
  const uid = auth.currentUser ? auth.currentUser.uid : null;

  useEffect(() => {
    setGrid(generateWordSearch());
    setGameStartTime(Date.now()); // Start the game timer
  }, []);

  useEffect(() => {
    if (lives <= 0 || foundWords.length === words.length) {
      calculateFinalScore(); // Calculate final score based on time taken
      setGameOver(true);
    }
  }, [lives, foundWords]);

  async function checkGameStarted() {
    const queryParams = new URLSearchParams(window.location.search);
    const gameId = queryParams.get("id");
    if (!gameId) return;

    const userId = auth.currentUser.uid;
    const subCollectionPath = `gameplay/${userId}/gameplays/`;

    try {
      const gameDoc = await getDocument(subCollectionPath, gameId);
      if (gameDoc?.gameStarted) {
        const redirectUrl = `${window.location.origin}/instructions/2048`;
        window.location.href = redirectUrl;
      }
    } catch (error) {
      console.error("Error checking game status:", error);
    }
  }

  async function gameOverDBUpdate() {
    const queryParams = new URLSearchParams(window.location.search);
    var gameId = queryParams.get("id");
    const userId = auth.currentUser.uid;
    const subCollectionPath = `gameplay/${userId}/gameplays/`;
    let currentScore = score;
    const gameplayData = {
      lives: lives,
      score: currentScore,
      // total_questions : total_questions
    };
    await updateDocument(subCollectionPath, gameId, gameplayData);
    await totalGameDataUpdate(currentScore);
  }

  async function totalGameDataUpdate(score) {
    const subCollectionPath = `gameplay`;
    const userId = auth.currentUser.uid;
    var docData = await getDocument(subCollectionPath, userId);
    let oldScore = docData.score;
    let total_games = docData.games_played + 1;
    let currentScore = score + oldScore;
    const gameplayData = {
      games_played: total_games,
      score: currentScore,
      // total_questions : total_questions
    };
    await updateDocument(subCollectionPath, userId, gameplayData);
  }

  // Countdown for the next question
  useEffect(() => {
    if (gameOver || gamePaused) return;

    const countdown = setInterval(() => {
      setNextQuestionTimer((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(countdown);
          setNextQuestionTimer(15);
          showQuestionPopup();
          return 15;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(countdown);
  }, [gameOver, gamePaused]);

  const calculateFinalScore = () => {
    if (!gameStartTime) return;
    const timeTaken = (Date.now() - gameStartTime) / 1000; // Calculate time in seconds
    const maxTimeBonus = 2000; // Example max bonus for completing within a very short time
    const baseCompletionBonus = 200; // Fixed bonus for completing the game

    // Time Bonus scales down based on how close the player finishes to the game start
    const timeBonus = Math.max(0, maxTimeBonus - Math.floor(timeTaken * 10));
    const finalScore = score + baseCompletionBonus + timeBonus;

    setScore(finalScore);
  };

  const showQuestionPopup = () => {
    setGamePaused(true);
  };

  const createEmptyGrid = () =>
    Array(gridSize)
      .fill(null)
      .map(() => Array(gridSize).fill(""));

  const placeWordInGrid = (grid, word) => {
    const directions = [
      { x: 1, y: 0 },
      { x: 0, y: 1 },
      { x: 1, y: 1 },
      { x: 1, y: -1 },
    ];
    let placed = false;
    while (!placed) {
      const dir = directions[Math.floor(Math.random() * directions.length)];
      const startX = Math.floor(Math.random() * gridSize);
      const startY = Math.floor(Math.random() * gridSize);

      if (canPlaceWord(grid, word, startX, startY, dir)) {
        for (let i = 0; i < word.length; i++) {
          grid[startY + i * dir.y][startX + i * dir.x] = word[i];
        }
        placed = true;
      }
    }
  };

  const canPlaceWord = (grid, word, startX, startY, dir) => {
    for (let i = 0; i < word.length; i++) {
      const newX = startX + i * dir.x;
      const newY = startY + i * dir.y;

      if (
        newX < 0 ||
        newX >= gridSize ||
        newY < 0 ||
        newY >= gridSize ||
        (grid[newY][newX] !== "" && grid[newY][newX] !== word[i])
      ) {
        return false;
      }
    }
    return true;
  };

  const fillGrid = (grid) => {
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    for (let y = 0; y < gridSize; y++) {
      for (let x = 0; x < gridSize; x++) {
        if (grid[y][x] === "") {
          grid[y][x] = alphabet[Math.floor(Math.random() * alphabet.length)];
        }
      }
    }
  };

  const generateWordSearch = () => {
    const newGrid = createEmptyGrid();
    words.forEach((word) => placeWordInGrid(newGrid, word));
    fillGrid(newGrid);
    return newGrid;
  };

  const handleCellClick = (rowIndex, cellIndex) => {
    if (gameOver || gamePaused) return;

    const newSelection = [...selectedCells, { row: rowIndex, col: cellIndex }];
    setSelectedCells(newSelection);

    const selectedWord = newSelection
      .map((cell) => grid[cell.row][cell.col])
      .join("");
    if (words.includes(selectedWord)) {
      const newFoundWords = [...foundWords, selectedWord];
      const newFoundCells = [...foundCells, [...newSelection]];
      setFoundWords(newFoundWords);
      setFoundCells(newFoundCells);
      setSelectedCells([]);

      // Apply combo multiplier and reset combo timer
      setScore((prevScore) => prevScore + 50 * comboMultiplier);
      setComboMultiplier(comboMultiplier + 2);
      if (comboTimer) clearTimeout(comboTimer);
      setComboTimer(setTimeout(() => setComboMultiplier(1), 10000)); // Reset combo multiplier after 10 seconds
    } else if (selectedWord.length > Math.max(...words.map((w) => w.length))) {
      setSelectedCells([]);
    }
  };

  const handleResetSelection = () => setSelectedCells([]);

  const isSelected = (rowIndex, cellIndex) =>
    selectedCells.some(
      (cell) => cell.row === rowIndex && cell.col === cellIndex
    );

  const isPartOfFoundWord = (rowIndex, cellIndex) =>
    foundCells.some((wordCells) =>
      wordCells.some((cell) => cell.row === rowIndex && cell.col === cellIndex)
    );

  const handlePopupClose = () => setGamePaused(false);

  const handleIncorrectAnswer = () => {
    if (lives - 1 <= 0) {
      setGameOver(true);
    } else {
      setLives(lives - 1);
    }
  };

  const handleRestartGame = () => {
    setGrid(generateWordSearch());
    setSelectedCells([]);
    setFoundWords([]);
    setFoundCells([]);
    setScore(0);
    setLives(3);
    setGameStartTime(Date.now());
    setGameOver(false);
    setComboMultiplier(1);
    setNextQuestionTimer(15);
    setGamePaused(false);
  };

  const exitGame = () => {
    setGameOver(true);
    window.location.href = "/games";
  };
  // Firestore update for Word Search game data
  useEffect(() => {
    if (uid) {
      const updatedData = {
        game_id: "WordSearch",
        played_at: new Date().toUTCString(),
        score: score,
        lives: lives,
        found_words: foundWords,
        uid: uid,
      };

      updateDocumentInSubcollection(
        "gameplay",
        uid,
        "gameplays",
        "WordSearch",
        updatedData
      )
        .then(() => console.log("WordSearch document successfully updated!"))
        .catch((error) =>
          console.error("Error updating WordSearch document:", error)
        );
    }
  }, [score, lives, foundWords, uid]);

  const renderHearts = () => {
    return Array.from({ length: 3 }).map((_, index) => (
      <span
        key={index}
        className={`text-red-500 text-2xl ${index < lives ? "opacity-100" : "opacity-30"}`}
      >
        ❤️
      </span>
    ));
  };

  return (
    <div className="min-h-screen bg-[var(--background-color)] py-8 px-4 sm:px-6 lg:px-8 animate-fadeIn">
      <div className="max-w-7xl mx-auto">
        {/* Game Title with Gradient */}
        <h1 className="font-['Jersey_25',sans-serif] text-4xl md:text-5xl font-bold text-center mb-10 text-yellow-500 bg-gradient-to-r from-[var(--primary-color)] via-[var(--interactive-color)] to-[var(--accent-color)] text-transparent bg-clip-text transform transition-all">
          Word Search
        </h1>

        {/* Game Stats and Controls */}
        <div className="text-white text-3xl w-full flex justify-between items-center p-4">
          <div className="flex items-center gap-6">
            <h1 className="text-2xl font-bold text-[var(--text-color)]">
              Score: {score}
            </h1>
            <h1 className="text-2xl font-bold text-[var(--text-color)]">
              Lives: {renderHearts()}
            </h1>
          </div>

          <div className="flex items-center gap-4">
            <button
              className="group relative inline-flex items-center px-6 py-3 bg-[var(--interactive-color)] text-white rounded-lg text-xl font-bold transform transition-all duration-300 hover:bg-opacity-90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--interactive-color)] focus:ring-offset-2 focus:ring-offset-[var(--background-color)]"
              onClick={handleResetSelection}
              disabled={selectedCells.length === 0}
            >
              <MdOutlineRestartAlt className="mr-2 text-xl" />
              Reset Selection
            </button>

            <button
              className="group relative inline-flex items-center px-6 py-3 bg-[var(--error-color)] text-white rounded-lg text-xl font-bold transform transition-all duration-300 hover:bg-opacity-90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--error-color)] focus:ring-offset-2 focus:ring-offset-[var(--background-color)]"
              onClick={exitGame}
            >
              Exit
            </button>
          </div>
        </div>

        {/* Next Question Timer */}
        <div className="text-center text-2xl font-bold text-[var(--text-color)] mb-6">
          Next Question In: {nextQuestionTimer}s
        </div>

        {/* Word Search Grid */}
        <div className="grid gap-1 justify-center mb-8">
          {grid.map((row, rowIndex) => (
            <div key={rowIndex} className="flex gap-1">
              {row.map((cell, cellIndex) => (
                <span
                  key={cellIndex}
                  className={`w-10 h-10 flex items-center justify-center text-2xl font-bold border border-[var(--border-color)] rounded-lg cursor-pointer ${
                    isSelected(rowIndex, cellIndex)
                      ? "bg-[var(--interactive-color)] text-white"
                      : isPartOfFoundWord(rowIndex, cellIndex)
                      ? "bg-green-500 text-white"
                      : "bg-[var(--surface-color)] text-[var(--text-color)]"
                  }`}
                  onClick={() => handleCellClick(rowIndex, cellIndex)}
                >
                  {cell}
                </span>
              ))}
            </div>
          ))}
        </div>

        {/* Word Bank */}
        <h2 className="font-['Jersey_25',sans-serif] text-3xl font-bold text-center mb-4 text-[var(--text-color)]">
          Word Bank
        </h2>
        <ul className="flex flex-wrap justify-center gap-4 mb-8">
          {words.map((word, index) => (
            <li
              key={index}
              className={`text-2xl font-bold ${
                foundWords.includes(word)
                  ? "text-[var(--accent-color)] line-through"
                  : "text-[var(--text-color)]"
              }`}
            >
              {word}
            </li>
          ))}
        </ul>

        {/* Game Over Screen */}
        {gameOver && (
          <GameOverScreen
            score={score}
            onRestart={handleRestartGame}
            onExit={exitGame}
          />
        )}

        {/* Popup */}
        <Popup
          onClose={handlePopupClose}
          onIncorrectAnswer={handleIncorrectAnswer}
          score={score}
          setScore={setScore}
          lives={lives}
          setLives={setLives}
          useLives={true}
          gameOver={gameOver}
        />
      </div>
    </div>
  );

};

export default WordSearch;
